import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Text,
  useOptionalAutoFocus,
} from "@salestrak/ui";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import type { useAvailableCustomFields } from "#app/hooks/issue-view/use-available-custom-fields";

type AvailableField = ReturnType<typeof useAvailableCustomFields>[number];

// The list's own scroll-end slack, so the last row clears the pinned footer
// and the thumb (the global-search sheet's gutter).
const LIST_BOTTOM_GUTTER = "not-empty:pb-12";

// The footer's bottom pad: the default variant's safe-area pad, lifted by the
// drawer's measured keyboard inset while a keyboard is up (`0px` otherwise).
const COMMIT_FOOTER =
  "pb-[calc(max(env(safe-area-inset-bottom,0px),var(--drawer-keyboard-inset,0px))+--spacing(4))]";

/**
 * The phone "add field from workspace" sheet, shared by the drawer's Custom
 * fields section and the summary widget. The Views/Filters sheet grammar:
 * title row (with the manage door on its trailing edge), the bordered inset
 * search band on the header's 24px line, one hairline splitting the chrome
 * from the scrolling rows, and the commit pinned in the footer as a
 * full-width button — never a round check in the title row.
 *
 * The list does not filter itself: the host passes the matched rows and
 * owns the query, the draft selection and the commit.
 */
export function AddFieldsDrawer({
  open,
  onOpenChange,
  fields,
  selectedIds,
  onToggle,
  canManage,
  onManage,
  onAdd,
  query,
  onQueryChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rows matching the query — the host filters. */
  fields: AvailableField[];
  selectedIds: string[];
  onToggle: (customFieldId: string) => void;
  canManage: boolean;
  /** The manager door — closes this sheet and opens the workspace fields dialog. */
  onManage: () => void;
  onAdd: () => void;
  query: string;
  onQueryChange: (query: string) => void;
}) {
  const [drawerSettled, setDrawerSettled] = useState(false);
  // The Drawer reports settle directly, through this one callback.
  const handleSheetSettle = useCallback(
    (open: boolean) => setDrawerSettled(open),
    [],
  );
  // The add-field search narrows a list; typing is optional, so the caret
  // lands only where a hardware keyboard is implied, and only once the
  // drawer has settled (a keyboard rising mid-animation desyncs its
  // translate math).
  const canAutoFocus = useOptionalAutoFocus() && drawerSettled;
  const count = selectedIds.length;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      // `onOpenChangeComplete` is the settle signal: it gates the search
      // autofocus so the keyboard never opens mid-animation.
      onOpenChangeComplete={handleSheetSettle}
      size="full"
    >
      <DrawerPopup showBar>
        <DrawerHeader>
          <Box direction="row" align="center" justify="between" gap="sm">
            <DrawerTitle>Add Custom Fields</DrawerTitle>
            {/* The manager door rides the title row — the desktop picker's
                "Create new" verb, as the squared outline offer (a ghost
                read as a label, not a door). */}
            {canManage ? (
              <Button variant="outline" size="sm" shape="square" onClick={onManage}>
                Create new
              </Button>
            ) : null}
          </Box>
        </DrawerHeader>
        {/* No rest highlight: the rows toggle on press and Enter picks
            nothing, so a highlighted first row would promise a pick. */}
        <Command
          autoHighlight={false}
          mode="none"
          value={query}
          onValueChange={onQueryChange}
        >
          {/* The bordered inset search on its own band under the title, on
              the header's 24px inset (the Views sheet), with ONE hairline
              under it so the pinned chrome and the scrolling rows read as two
              regions (the global-search sheet's seam). */}
          <Box border="bottom" paddingBottom="sm" paddingX="lg" shrink={false}>
            <CommandInput
              inset
              placeholder="Search Custom Fields..."
              autoFocus={canAutoFocus}
            />
          </Box>
          {/* `workspace`: CommandList already owns a ScrollArea, so the panel
              is only the bounded flex region. The rows carry an 8px inset
              inside the list's 8px gutter; the panel's 8px puts their
              checkboxes on the 24px line. */}
          <DrawerPanel variant="workspace" className="px-2">
            <CommandList className={LIST_BOTTOM_GUTTER}>
              {/* No result count: the rows are the answer. */}
              <CommandGroup>
                {fields.map((field) => {
                  const isSelected = selectedIds.includes(field.customFieldId);
                  const meta = getFieldTypeMeta(field.type);
                  // The key alone under the name: it is what tells two
                  // same-named fields apart. The creator is desktop table
                  // fare, not a phone row's.
                  const desc = field.key;
                  return (
                    // Base UI's autocomplete item has no `onSelect`; the row
                    // owns the press and the box is a state glyph, not a
                    // second hit target.
                    <CommandItem
                      key={field.customFieldId}
                      value={field.customFieldId}
                      onClick={() => onToggle(field.customFieldId)}
                    >
                      <Box direction="row" align="center" gap="sm" fill="width" minWidth={0}>
                        <Checkbox checked={isSelected} nonInteractive />
                        <Icon name={meta.icon} size="md" color={meta.color} />
                        <Box direction="column" grow gap="xs" overflow="hidden" minWidth={0}>
                          <Text size="sm" weight="medium" truncate={1}>
                            {field.name}
                          </Text>
                          {desc ? (
                            <Text size="xs" variant="muted" truncate={1}>
                              {desc}
                            </Text>
                          ) : null}
                        </Box>
                      </Box>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {/* The Command holds no items of its own (`mode="none"`, the
                  host filters), so Base UI would paint the empty state under
                  a full list; it renders only when there is nothing. */}
              {fields.length === 0 ? (
                <CommandEmpty>No Custom Fields found.</CommandEmpty>
              ) : null}
            </CommandList>
          </DrawerPanel>
        </Command>
        {/* The commit foots the sheet, pinned under the scroller on the
            footer's hairline, full width at the 40px offer tier (the Filters
            sheet's Clear all). The count rides the verb, so the button says
            what it will do. Its bottom pad is the larger of the home
            indicator and the drawer's measured keyboard inset, so the button
            rides the keys' top edge while the search holds focus (a row tap
            keeps the caret in the field, so the commit must stay reachable)
            and settles onto the safe area when the keyboard drops. No
            transition: the inset is measured, not animated. */}
        <DrawerFooter className={COMMIT_FOOTER}>
          <Button size="xl" shape="square" fullWidth disabled={count === 0} onClick={onAdd}>
            {count === 0
              ? "Add fields"
              : `Add ${count} field${count === 1 ? "" : "s"}`}
          </Button>
        </DrawerFooter>
      </DrawerPopup>
    </Drawer>
  );
}
